import { ChangeDetectorRef, Component, inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { StripeElementsOptions, StripePaymentElementOptions } from '@stripe/stripe-js';
import { StripePaymentElementComponent, StripeService } from 'ngx-stripe';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MarketplaceCheckoutService } from 'src/app/services/marketplace/checkout.service';
import { CartService } from 'src/app/services/marketplace/cart.service';
import { CartLine, PaymentIntentResponse } from 'src/app/services/marketplace/marketplace.models';

// Stripe draws the card form in its own frame, on its own site, so it can't
// read the site's CSS: the colors below are the theme tokens' values
// (styles/_tokens.scss), and the font comes from Google Fonts.
const STRIPE_FONT_CSS = 'https://fonts.googleapis.com/css2?family=Titillium+Web:wght@400;600;700&display=swap';
const LINE = 'rgba(148, 178, 208, 0.18)';        // --wt-color-border
const LINE_STRONG = 'rgba(148, 178, 208, 0.34)'; // --wt-color-border-strong
const GOLD = '#c9a15c';                          // --wt-color-accent
const STRIPE_APPEARANCE: StripeElementsOptions['appearance'] = {
  theme: 'night',
  labels: 'above',
  variables: {
    fontFamily: '"Titillium Web", -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
    fontSizeBase: '15px',
    colorPrimary: GOLD,
    colorBackground: '#0b1c2e',     // --wt-color-surface-sunken
    colorText: '#eef2f6',           // --wt-color-text
    colorTextSecondary: '#8a9db4',  // --wt-color-text-muted
    colorTextPlaceholder: '#5f7590', // --wt-color-text-faint
    colorDanger: '#f0606f',
    colorSuccess: '#54a636',
    borderRadius: '10px',
    spacingUnit: '4px',
    gridRowSpacing: '18px',
    gridColumnSpacing: '14px',
    focusBoxShadow: 'none',
    focusOutline: 'none',
  },
  rules: {
    '.Label': { fontWeight: '600', fontSize: '13px', color: '#8a9db4', marginBottom: '6px' },
    '.Input': { border: `1px solid ${LINE}`, boxShadow: 'none', padding: '12px 14px' },
    '.Input:hover': { borderColor: LINE_STRONG },
    '.Input:focus': { borderColor: GOLD, boxShadow: 'none' },
    '.Input--invalid': { borderColor: '#f0606f', boxShadow: 'none' },
    '.Error': { fontSize: '13px' },
    '.Tab': { border: `1px solid ${LINE}`, backgroundColor: '#0b1c2e', boxShadow: 'none', padding: '12px 14px' },
    '.Tab:hover': { borderColor: LINE_STRONG, color: '#eef2f6' },
    '.Tab--selected, .Tab--selected:hover, .Tab--selected:focus': {
      borderColor: GOLD, backgroundColor: '#102539', color: '#eef2f6', boxShadow: 'none',
    },
    '.TabLabel': { fontWeight: '600' },
    '.TabIcon--selected': { fill: GOLD },
    '.Block': { backgroundColor: '#0b1c2e', border: `1px solid ${LINE}`, boxShadow: 'none' },
    '.PickerItem': { backgroundColor: '#0b1c2e', border: `1px solid ${LINE}`, boxShadow: 'none' },
  },
};

@Component({
  selector: 'app-marketplace-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CheckoutComponent implements OnInit {
  @ViewChild(StripePaymentElementComponent) paymentElement!: StripePaymentElementComponent;

  loading = true;
  paying = false;
  errorMessage: string | null = null;
  /** The checkout was refused because the login expired. */
  needsSignIn = false;
  intent: PaymentIntentResponse | null = null;
  /** What's being bought, for the order summary (the cart isn't emptied until payment). */
  lines: CartLine[] = [];

  elementsOptions: StripeElementsOptions = { locale: 'en', clientSecret: '' };
  paymentElementOptions: StripePaymentElementOptions = {
    layout: { type: 'tabs', defaultCollapsed: false },
  };
  /** Stripe's form has drawn itself (placeholder fields show until then). */
  paymentReady = false;
  /** Why the last payment attempt failed; stays on screen until the next try. */
  payError: string | null = null;

  stripe = inject(StripeService);

  constructor(
    private checkoutService: MarketplaceCheckoutService,
    private cartService: CartService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cartService.getCart()
      .pipe(catchError(() => of(null)))
      .subscribe((cart) => {
        this.lines = cart?.lines.filter((line) => !line.saved_for_later) ?? [];
        this.cdr.detectChanges();
      });

    // Fetch a real client_secret before rendering Elements — the previous
    // subscription checkout component (strip.component.ts) never did this;
    // it initialized elementsOptions.clientSecret to '' and left it there.
    // The discount code applied in the cart (if any) comes along.
    const coupon = this.cartService.appliedCoupon;
    this.checkoutService.createPaymentIntent(coupon).subscribe({
      next: (intent) => {
        this.intent = intent;
        this.elementsOptions = {
          locale: 'en',
          clientSecret: intent.client_secret,
          fonts: [{ cssSrc: STRIPE_FONT_CSS }],
          appearance: STRIPE_APPEARANCE,
        };
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.loading = false;
        this.needsSignIn = err?.status === 401;
        if (coupon && err?.status === 400) {
          // e.g. the code expired meanwhile: drop it so the cart isn't stuck on it
          this.cartService.appliedCoupon = null;
        }
        this.errorMessage = this.needsSignIn
          ? 'Your sign-in has expired. Please sign in again to check out.'
          : err?.error?.[0] || err?.error?.detail || 'Could not start checkout. Your cart may be empty or below the $5 minimum.';
        this.cdr.detectChanges();
      },
    });
  }

  money(value: string | number | null | undefined): string {
    return `$${(Number(value) || 0).toFixed(2)}`;
  }

  onPaymentReady(): void {
    this.paymentReady = true;
    this.cdr.detectChanges();
  }

  pay(): void {
    if (this.paying || !this.paymentReady || !this.paymentElement) {
      return;
    }
    this.paying = true;
    this.payError = null;

    this.stripe
      .confirmPayment({
        elements: this.paymentElement.elements,
        confirmParams: {
          // Cash App Pay, Amazon Pay, Klarna etc. send the buyer to approve the
          // payment on their own site; Stripe brings them back here, adding
          // redirect_status. Cards stay on the page and never use it.
          return_url: new URL(
            `marketplace/checkout/success?order=${encodeURIComponent(this.intent?.order_number ?? '')}`,
            document.baseURI,
          ).href,
        },
        redirect: 'if_required',
      })
      .subscribe((result) => {
        this.paying = false;
        this.cdr.detectChanges();

        if (result.error) {
          // A missing or mistyped field: Stripe already marks it on the form.
          if (result.error.type === 'validation_error') {
            return;
          }
          this.payError = result.error.message || "The payment didn't go through.";
          this.cdr.detectChanges();
          return;
        }
        const status = result.paymentIntent?.status;
        if (status === 'succeeded' || status === 'processing') {
          this.cartService.appliedCoupon = null; // used up by this order
          this.router.navigate(['/marketplace/checkout/success'], {
            queryParams: {
              order: this.intent?.order_number,
              ...(status === 'processing' ? { redirect_status: 'processing' } : {}),
            },
          });
          return;
        }
        // Declined or cancelled in a payment app (e.g. Cash App's QR flow):
        // Stripe hands the payment back unpaid instead of an error.
        this.payError = 'It was declined or cancelled in the payment app.';
        this.cdr.detectChanges();
      });
  }
}
